/**
 * ScolaireLayout — shell partagé du module Scolaire / Cantine (R13).
 *
 * Jumeau de ServiceTechniqueLayout, accent « cantine » (ambre). Garantit que la
 * sub-nav reste affichée sur toutes les pages du module.
 *
 * Deux jeux d'onglets selon le rôle (décision produit) :
 *   - manager / admin / super_admin : pilotage complet
 *   - agent : vue terrain réduite (son planning + opérationnel du jour)
 * L'agent sans service scolaire est traité comme un administré en amont (routes).
 */

import { type ReactNode, useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Landmark, ChevronDown } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { LogoMark, SectionEyebrow } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AppHeader } from '@/components/AppHeader';

export type ScolaireTab =
  | 'dashboard' | 'planning' | 'presence-agents' | 'familles' | 'cantine'
  | 'periscolaire' | 'alsh' | 'ecoles' | 'calendrier' | 'pointage' | 'menus' | 'parametres'
  | 'facturation' | 'tarifs' | 'effectifs-jour';

const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-cantine) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

export function ScolaireLayout({
  activeTab,
  children,
  backLink,
}: {
  activeTab: ScolaireTab;
  children: ReactNode;
  backLink?: { to: string; label: string };
}) {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();

  const canManage = !!user && ['super_admin', 'admin', 'manager'].includes(user.role);
  const isAgent = user?.role === 'agent';
  // Écoles & classes + Calendrier = structure de la commune, réservée au
  // maire / adjoints (admin). Le manager cantine ne les voit pas.
  const isAdmin = !!user && ['super_admin', 'admin'].includes(user.role);

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Scolaire / Cantine" accent="cantine" />

      <nav
        aria-label="Sections scolaire"
        className="sticky top-[57px] z-20 border-b border-border bg-surface/60 backdrop-blur-glass"
      >
        <div className="flex flex-wrap items-center gap-1 px-4 py-2 sm:px-6">
          {backLink && (
            <button
              type="button"
              onClick={() => navigate(backLink.to)}
              className="mr-2 inline-flex h-9 items-center gap-1 rounded-r-md px-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface/60 hover:text-cantine-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine focus-visible:ring-offset-2 focus-visible:ring-offset-bg whitespace-nowrap"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {backLink.label}
            </button>
          )}

          {isAgent ? (
            <>
              <MenuDeroulant
                label="Suivi quotidien"
                active={activeTab === 'planning' || activeTab === 'presence-agents' || activeTab === 'pointage' || activeTab === 'cantine' || activeTab === 'periscolaire' || activeTab === 'alsh' || activeTab === 'effectifs-jour'}
                items={[
                  { to: '/scolaire/mon-planning', label: 'Planning' },
                  { to: '/scolaire/pointage', label: 'Pointage' },
                  { to: '/scolaire/effectifs-jour', label: 'Présences du jour — vue consolidée' },
                  { to: '/scolaire/cantine', label: 'Cantine du jour' },
                  { to: '/scolaire/periscolaire', label: 'Périscolaire du jour' },
                  { to: '/scolaire/alsh', label: 'ALSH du jour' },
                ]}
                onNavigate={navigate}
              />
              <Pill to="/scolaire/menus" label="Menus" active={activeTab === 'menus'} />
              <Pill to="/scolaire/familles" label="Familles" active={activeTab === 'familles'} />
            </>
          ) : (
            <>
              <Pill to="/scolaire" label="Tableau de bord" active={activeTab === 'dashboard'} />
              <MenuDeroulant
                label="Suivi quotidien"
                active={activeTab === 'planning' || activeTab === 'presence-agents' || activeTab === 'pointage' || activeTab === 'cantine' || activeTab === 'periscolaire' || activeTab === 'alsh' || activeTab === 'effectifs-jour'}
                items={[
                  { to: '/scolaire/planning', label: 'Planning' },
                  { to: '/scolaire/pointage', label: 'Pointage' },
                  { to: '/scolaire/effectifs-jour', label: 'Présences du jour — vue consolidée' },
                  { to: '/scolaire/cantine', label: 'Cantine du jour' },
                  { to: '/scolaire/periscolaire', label: 'Périscolaire du jour' },
                  { to: '/scolaire/alsh', label: 'ALSH du jour' },
                ]}
                onNavigate={navigate}
              />
              <Pill to="/scolaire/familles" label="Familles & enfants" active={activeTab === 'familles'} />
              <Pill to="/scolaire/menus" label="Menus" active={activeTab === 'menus'} />
              <Pill to="/scolaire/facturation" label="Facturation" active={activeTab === 'facturation'} />
              {isAdmin && (
                <MenuDeroulant
                  label="Configuration"
                  active={activeTab === 'ecoles' || activeTab === 'tarifs' || activeTab === 'calendrier' || activeTab === 'parametres'}
                  items={[
                    { to: '/scolaire/ecoles', label: 'Écoles & classes' },
                    { to: '/scolaire/tarifs', label: 'Tarifs' },
                    { to: '/scolaire/calendrier', label: 'Calendrier' },
                    { to: '/scolaire/parametres', label: 'Paramètres' },
                    { to: '/scolaire/prestataires', label: 'Prestataires délégués' },
                  ]}
                  onNavigate={navigate}
                />
              )}
            </>
          )}
        </div>
      </nav>

      {children}
    </div>
  );
}

function MenuDeroulant({
  label, active, items, onNavigate,
}: {
  label: string;
  active: boolean;
  items: { to: string; label: string }[];
  onNavigate: (to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  function toggle() {
    setOpen((o) => {
      const next = !o;
      if (next && btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        setPos({ top: r.bottom + 4, left: r.left });
      }
      return next;
    });
  }
  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(t) && btnRef.current && !btnRef.current.contains(t)) setOpen(false);
    }
    function onScrollResize() { setOpen(false); }
    document.addEventListener('mousedown', onOutside);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={[
          'inline-flex h-9 items-center gap-1 rounded-r-pill px-4 text-sm font-medium transition-all whitespace-nowrap',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          active ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70 hover:text-ink',
        ].join(' ')}
      >
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}
          className="min-w-[200px] rounded-r-md border border-border bg-surface shadow-r-lg p-1"
        >
          {items.map((it) => (
            <button
              key={it.to}
              role="menuitem"
              type="button"
              onClick={() => { setOpen(false); onNavigate(it.to); }}
              className="block w-full rounded-r-sm px-3 py-2 text-left text-xs text-ink hover:bg-cantine-tint/40"
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({
  to, label, active, disabled,
}: {
  to: string;
  label: string;
  active: boolean;
  disabled?: boolean;
}) {
  if (disabled) {
    return (
      <span
        className="inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium text-ink-subtle cursor-not-allowed whitespace-nowrap"
        title="Section réservée aux rôles managériaux"
      >
        {label}
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      end={to === '/scolaire'}
      className={() =>
        [
          'inline-flex h-9 items-center rounded-r-pill px-4 text-sm font-medium transition-all whitespace-nowrap',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          active ? 'bg-cantine text-white shadow-soft' : 'text-ink-muted hover:bg-surface/70 hover:text-ink',
        ].join(' ')
      }
    >
      {label}
    </NavLink>
  );
}
